import { useCallback, useEffect, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { IoIosArrowDown, IoIosArrowUp } from 'react-icons/io';
import { Server } from '@wajeez/wajeez.microservices';
import { IoIosSettings } from 'react-icons/io';
import { getLocalTimeFromUTC } from '@wajeez-web/core/utils';
import { AiOutlineCheck, AiOutlineClose } from 'react-icons/ai';
import { MediaHelpers } from '@wajeez-web/core/api';
import { contentEntityDataType } from '@wajeez-web/stores/content';
import { useCommonSelector } from '@wajeez-web/stores/common';
import { useTranslation } from 'react-i18next';

import { Pagination, TablePagination } from './TablePagination';
import { Popover, Dropdown, CheckboxGroup, CheckBoxType } from '../..';
import {
  FilterOption,
  TableFilter,
  selectFilterOperators,
} from './TableFilter';
import { useFilterInput } from './useFilterInput';
import { ReturnTranslatedText } from '@wajeez-web/core/localization';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { FormField } from '@wajeez-web/components/form-builder';
import { Tooltip } from '@mui/material';
import { TableActionType } from '@wajeez-web/components/data-table';
import { useAuthSelector } from '@wajeez-web/stores/auth';
import { useSearchParams } from 'react-router-dom';

type TableHeaderType = {
  key: string | undefined;
  title: string | undefined;
  isHidden: boolean | undefined;
  isFilterable: boolean | undefined;
  dataType: Server.DataType | undefined;
  options: string[] | undefined;
  actionName: string | undefined;
};

type RowType = { id: string; [key: string]: string | JSX.Element | undefined };

interface DataTableProps {
  dataTableData: contentEntityDataType | null;
  onHeaderManage?: (header: CheckBoxType[] | undefined) => void;
  onPaginationChange?: (pagination: Pagination) => void;
  sort?:
    | {
        property?: number | undefined;
        orderByDirection?: Server.OrderByDirection | undefined;
      }
    | undefined;
  onSort?: (sort: {
    property: number | undefined;
    orderByDirection: Server.OrderByDirection;
  }) => void;
  onFilter?: (filters: FilterOption[], pagination: Pagination) => void;
  rowActions?: (row: RowType) => TableActionType[];
  renderRowActions?: (row: RowType) => JSX.Element;
  mapRowsCustomized?: (rows: RowType[] | undefined) => RowType[] | undefined;
  isLoading?: boolean;
  extraActions?: {
    label: string;
    onClick: () => void;
    Icon?: JSX.Element;
  }[];
  defaultFilters?: {
    key: string;
    operator: Server.ComparisonType;
    dataType: Server.DataType;
    label: string;
    fieldMeta: FormField;
    onChange?: (value: any) => void;
  }[];
  forcePaginationReset?: boolean;
  onForcePaginationReset?: () => void;
}

export const DataTable = ({
  dataTableData,
  onHeaderManage,
  onPaginationChange,
  sort,
  onSort,
  onFilter,
  rowActions,
  renderRowActions,
  mapRowsCustomized,
  isLoading,
  extraActions,
  defaultFilters,
  forcePaginationReset,
  onForcePaginationReset,
}: DataTableProps) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const pageSearchParam = searchParams.get('page');
  const pageSizeSearchParam = searchParams.get('pageSize');

  const [headerOriginal, setHeaderOriginal] = useState<
    TableHeaderType[] | undefined
  >();
  const [header, setHeader] = useState<TableHeaderType[] | undefined>();
  const [hoveredHeader, setHoveredHeader] = useState<
    | {
        property?: number | undefined;
        orderByDirection?: Server.OrderByDirection | undefined;
      }
    | undefined
  >();
  const [rowsOriginal, setRowsOriginal] = useState<RowType[] | undefined>();
  const [rows, setRows] = useState<RowType[] | undefined>();
  const [shownMenuFilters, setShownMenuFilters] = useState<
    FilterOption[] | undefined
  >(undefined);
  const [selectedFilters, setSelectedFilters] = useState<
    {
      filter: FilterOption;
      operators: { value: string; label: string }[] | null;
    }[]
  >([]);
  const [appliedFilters, setAppliedFilters] = useState<FilterOption[]>([]);
  // const [pagination, setPagination] = useState<{
  //   pageNumber: number;
  //   pageSize: number;
  //   totalCount: number | undefined;
  // }>({
  //   pageNumber: pageSearchParam ? Number(pageSearchParam) : 1,
  //   pageSize: pageSizeSearchParam ? Number(pageSizeSearchParam) : 10,
  //   totalCount: undefined,
  // });

  const dir = useCommonSelector(({ common }) => common.settings.dir);
  const permission = useAuthSelector((state) => state.auth.permission);

  const { t } = useTranslation();

  const onFilterChange = (filters: FilterOption[]) => {
    // setPagination({ ...pagination, pageNumber: 1 });
    setSearchParams({
      page: '1',
      pageSize: pageSizeSearchParam || '',
    });

    onFilter &&
      onFilter(filters, {
        pageNumber: 1,
        pageSize: Number(pageSizeSearchParam),
        totalCount: dataTableData?.totalCount,
      });
  };

  const { renderFilterInput } = useFilterInput(
    appliedFilters,
    setAppliedFilters,
    onFilterChange
  );

  const determineDefaultFilterInputOperator = (
    dataType: Server.DataType | undefined,
    options: string[] | undefined
  ) => {
    if (dataType === Server.DataType.String && !options) {
      return Server.ComparisonType.Contain;
    } else if (dataType === Server.DataType.String && options) {
      return Server.ComparisonType.Equal;
    } else if (
      dataType === Server.DataType.Date ||
      dataType === Server.DataType.DateTime ||
      dataType === Server.DataType.Number
    ) {
      return Server.ComparisonType.GreaterOrEqual;
    } else {
      return Server.ComparisonType.Equal;
    }
  };

  const mapRows = useCallback(() => {
    let mappedData: RowType[] = [];

    dataTableData?.columns
      ?.filter((c) => !c.isHidden)
      .forEach((c) => {
        const columnValue = c.value;

        if (columnValue) {
          if (mappedData.length) {
            mappedData = mappedData.map((d, i) => {
              return {
                ...d,
                [columnValue.toString()]: c.cells?.[i],
              };
            });
          } else {
            c.cells?.forEach((e, i) => {
              mappedData.push({
                id: e + i.toString(),
                [columnValue.toString()]: e,
              });
            });
          }
        }
      });

    setRowsOriginal(mappedData);

    if (mapRowsCustomized) {
      return mapRowsCustomized(mappedData);
    }

    return mappedData;
  }, [dataTableData]);

  useEffect(() => {
    if (!pageSearchParam || !pageSizeSearchParam) {
      setSearchParams({
        page: '1',
        pageSize: '10',
      });
    }
  }, [pageSearchParam, pageSizeSearchParam, setSearchParams]);

  // useEffect(() => {
  //   setPagination((pagination) => {
  //     return {
  //       totalCount: pagination.totalCount,
  //       pageNumber: Number(pageSearchParam),
  //       pageSize: Number(pageSizeSearchParam),
  //     };
  //   });
  // }, [pageSearchParam, pageSizeSearchParam]);

  useEffect(() => {
    setShownMenuFilters(
      header
        ?.filter((h) => h.isFilterable && h.dataType !== Server.DataType.Image)
        .map((h) => {
          return {
            key: h.key,
            label: h.title,
            dataType: h.dataType,
          };
        })
    );
  }, [header?.length]);

  useEffect(() => {
    const headerOriginal = dataTableData?.columns?.map((c) => {
      return {
        key: c.value?.toString(),
        title: c.header,
        isHidden: c.isHidden,
        isFilterable: c.isFilterable,
        dataType: c.dataType,
        options: c.options,
        actionName: c.actionName,
      };
    });

    setHeaderOriginal(headerOriginal);

    if (headerOriginal) {
      if (renderRowActions) {
        setHeader([
          {
            key: 'actions',
            title: '',
            dataType: 0,
            isFilterable: false,
            isHidden: false,
            options: undefined,
            actionName: undefined,
          },
          ...headerOriginal,
          {
            key: 'filler',
            title: '',
            dataType: 0,
            isFilterable: false,
            isHidden: false,
            options: undefined,
            actionName: undefined,
          },
        ]);
      } else {
        setHeader([
          ...headerOriginal,
          {
            key: 'filler',
            title: '',
            dataType: 0,
            isFilterable: false,
            isHidden: false,
            options: undefined,
            actionName: undefined,
          },
        ]);
      }
    }
  }, [dataTableData, renderRowActions]);

  useEffect(() => {
    const rows = mapRows();

    setRows(rows);
  }, [mapRows]);

  // useEffect(() => {
  //   setPagination((pagination) => {
  //     return { ...pagination, totalCount: dataTableData?.totalCount };
  //   });
  // }, [dataTableData?.totalCount]);

  const mapColumnWidth = (type: Server.DataType) => {
    switch (type) {
      case Server.DataType.Number:
      case Server.DataType.NumberCurrency:
        return '150px';
      case Server.DataType.Image:
      case Server.DataType.Date:
      case Server.DataType.DateTime:
        return '170px';
      case Server.DataType.Trimmable:
        return '300px';
      case Server.DataType.Link:
        return '300px';
      default:
        return '230px';
    }
  };

  const renderHeader = () => {
    return header
      ?.filter((h) => !h.isHidden && h.title !== 'Id')
      .map((h) => {
        return h.key !== 'actions' ? (
          h.key === 'filler' ? (
            <TableCell
              key={h.key}
              sx={{
                width: '100% !important',
                minWidth: '100% !important',
                fontFamily: 'Tajawal !important',
                backgroundColor: '#dae7e8',
              }}
              style={{
                position: 'sticky',
                top: 0,
                zIndex: 10,
              }}
            >
              {h.title}
            </TableCell>
          ) : (
            <TableCell
              key={h.key}
              sx={{
                minWidth: mapColumnWidth(h.dataType || Server.DataType.String),
                fontWeight: 'bold',
                cursor: 'pointer',
                fontFamily: 'Tajawal !important',
                backgroundColor: '#dae7e8',
              }}
              style={{
                position: 'sticky',
                top: 0,
                zIndex: 10,
              }}
            >
              <div style={{ width: '100%' }}>
                <div
                  style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'flex-between',
                    alignItems: 'center',
                  }}
                  onClick={() => {
                    onSort &&
                      onSort({
                        property: Number(h.key),
                        orderByDirection:
                          sort?.property?.toString() !== h.key
                            ? Server.OrderByDirection.Descending
                            : sort?.orderByDirection ===
                              Server.OrderByDirection.Descending
                            ? Server.OrderByDirection.Ascending
                            : Server.OrderByDirection.Descending,
                      });
                  }}
                  onMouseEnter={() => {
                    setHoveredHeader({
                      property: Number(h.key),
                      orderByDirection:
                        sort?.property?.toString() !== h.key
                          ? Server.OrderByDirection.Descending
                          : sort?.orderByDirection ===
                            Server.OrderByDirection.Descending
                          ? Server.OrderByDirection.Ascending
                          : Server.OrderByDirection.Descending,
                    });
                  }}
                  onMouseLeave={() => {
                    setHoveredHeader(undefined);
                  }}
                >
                  <Tooltip placement="top" title={h.title}>
                    <div className="w-full h-[24px] overflow-hidden">
                      {h.title}
                    </div>
                  </Tooltip>
                  <div style={{ marginRight: '4px' }}>
                    {sort?.property?.toString() !== h.key ? (
                      hoveredHeader?.property?.toString() === h.key ? (
                        <IoIosArrowUp />
                      ) : (
                        <div style={{ width: '14px', height: '14px' }}></div>
                      )
                    ) : sort?.orderByDirection ===
                      Server.OrderByDirection.Descending ? (
                      <IoIosArrowDown />
                    ) : (
                      <IoIosArrowUp />
                    )}
                  </div>
                </div>
                <div style={{ marginTop: '8px' }}>
                  {renderFilterInput({
                    dataType: h.dataType,
                    key: h.key,
                    label: h.title,
                    operator: determineDefaultFilterInputOperator(
                      h.dataType,
                      h.options
                    ),
                    options: h.options,
                    onChange: (value) => {
                      const foundSelectedFilter = selectedFilters.find(
                        (sf) => sf.filter.key === h.key
                      );

                      if (!foundSelectedFilter) {
                        setShownMenuFilters(
                          shownMenuFilters?.filter((sf) => sf.key !== h.key)
                        );

                        setSelectedFilters([
                          ...selectedFilters,
                          {
                            filter: {
                              dataType: h.dataType,
                              key: h.key,
                              label: h.title,
                              operator: determineDefaultFilterInputOperator(
                                h.dataType,
                                h.options
                              ),
                            },
                            operators: selectFilterOperators(h.dataType),
                          },
                        ]);
                      } else {
                        //
                        // The following if statement is to remove the filter from the selectedFilters
                        // when the header input field value is cleared.
                        // This logic isn't made in the useFilterInput hook because this doesn't
                        // apply to the filter input fields that are added using the filter menu.
                        // The only common logic in the useFilterInput hook is the applied filters,
                        // since when a filter is removed from that list, this should be applied everywhere.
                        //

                        if (!value) {
                          setShownMenuFilters(
                            header
                              ?.filter((h) => h.isFilterable)
                              .map((h) => {
                                return {
                                  key: h.key,
                                  label: h.title,
                                  dataType: h.dataType,
                                };
                              })
                              .filter((h) => {
                                let isFilterApplied = false;

                                for (const af of appliedFilters) {
                                  if (af.key === h.key) {
                                    isFilterApplied = true;

                                    break;
                                  }
                                }

                                return (
                                  !isFilterApplied ||
                                  h.key === foundSelectedFilter.filter.key
                                );
                              })
                          );

                          setSelectedFilters(
                            selectedFilters.filter(
                              (sf) => sf.filter.key !== h.key
                            )
                          );
                        }
                      }
                    },
                  })}
                </div>
              </div>
            </TableCell>
          )
        ) : (
          <TableCell
            key={h.key}
            sx={{
              minWidth: '100px !important',
              fontFamily: 'Tajawal !important',
              backgroundColor: '#dae7e8',
            }}
            style={{
              position: 'sticky',
              top: 0,
              right: dir === 'rtl' ? 0 : undefined,
              left: dir === 'ltr' ? 0 : undefined,
              zIndex: 20,
            }}
          >
            {h.title}
          </TableCell>
        );
      });
  };

  const renderLinks = (value: string | undefined) => {
    if (value && value.startsWith('https://')) {
      const links = value.split('https://').map((l) => {
        if (l) {
          return 'https://' + l;
        } else {
          return '';
        }
      });

      return links.map((l) => {
        if (l) {
          return (
            <a
              key={l}
              href={l}
              target="_blank"
              rel="noreferrer"
              className="block text-blue-700"
            >
              {l}
            </a>
          );
        } else {
          return null;
        }
      });
    }

    return value;
  };

  const renderRowCellContent = (header: TableHeaderType, row: RowType) => {
    if (header.key && typeof row[header.key] === 'string') {
      const value = row[header.key] as string;

      if (header.dataType === Server.DataType.Date) {
        return value ? getLocalTimeFromUTC(value, 'YYYY-MM-DD') : '_';
      } else if (header.dataType === Server.DataType.DateTime) {
        return value ? getLocalTimeFromUTC(value) : '_';
      } else if (header.dataType === Server.DataType.Image) {
        if (value) {
          return (
            <div style={{ width: '80px', height: '80px' }}>
              <img
                src={MediaHelpers.getPublicMediaById(value)}
                alt={`cell-content-${row[header.key]}`}
                style={{ width: '100%', height: '100%' }}
              />
            </div>
          );
        } else {
          return '';
        }
      } else if (header.dataType === Server.DataType.Boolean) {
        if (value === 'True') {
          return (
            <div style={{ fontSize: '24px', color: 'green' }}>
              <AiOutlineCheck />
            </div>
          );
        } else {
          return (
            <div style={{ fontSize: '24px', color: 'red' }}>
              <AiOutlineClose />
            </div>
          );
        }
      } else if (header.dataType === Server.DataType.NumberCurrency) {
        return +Number(value).toFixed(3) + '$';
      } else if (header.dataType === Server.DataType.Trimmable) {
        const value = row[header.key] as string;

        return (
          <div style={{ wordBreak: 'break-word' }}>
            <Tooltip placement="top" title={value}>
              <div className="w-[300px] h-[70px] overflow-hidden">{value}</div>
            </Tooltip>
          </div>
        );
      } else if (header.dataType === Server.DataType.Link) {
        const value = row[header.key] as string;

        return (
          <div style={{ wordBreak: 'break-word' }}>{renderLinks(value)}</div>
        );
      } else if (header.dataType === Server.DataType.NumberMS) {
        const value = (
          Number(row[header.key] as string) /
          (1000 * 60 * 60)
        ).toFixed(0);

        return <div>{value + ' ' + t(`general.${'min'}`, 'min')}</div>;
      } else {
        const rowActionsMapped = rowActions && rowActions(row);

        const rowAction = rowActionsMapped?.find(
          (r) => r.title === header.actionName
        );

        const hasPermission = rowAction?.permission
          ? permission?.includes(rowAction.permission)
          : true;

        if (header.actionName && hasPermission) {
          return (
            <div
              style={{
                wordBreak: 'break-word',
                color: 'var(--theme-primary)',
                cursor: 'pointer',
              }}
              onClick={rowAction?.action}
            >
              {row[header.key]}
            </div>
          );
        } else {
          return (
            <div style={{ wordBreak: 'break-word' }}>{row[header.key]}</div>
          );
        }
      }
    } else if (header.key) {
      return row[header.key];
    } else {
      return null;
    }
  };

  const renderRows = () => {
    return rows?.map((r, i) => {
      return (
        <TableRow
          key={r.id}
          sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
        >
          {header
            ?.filter((h) => !h.isHidden && h.title !== 'Id')
            .map((h) => {
              return h.key !== 'actions' ? (
                <TableCell
                  key={h.key}
                  sx={{
                    fontFamily: 'Tajawal !important',
                    textAlign: dir === 'ltr' ? 'right' : 'left',
                  }}
                >
                  {renderRowCellContent(h, r)}
                </TableCell>
              ) : (
                <TableCell
                  key={h.key}
                  sx={{
                    fontFamily: 'Tajawal !important',
                    textAlign: dir === 'ltr' ? 'right' : 'left',
                  }}
                  style={{
                    position: 'sticky',
                    right: dir === 'rtl' ? 0 : undefined,
                    left: dir === 'ltr' ? 0 : undefined,
                    zIndex: 10,
                    backgroundColor: 'white',
                  }}
                >
                  {renderRowActions &&
                    rowsOriginal?.[i] &&
                    renderRowActions(rowsOriginal?.[i])}
                </TableCell>
              );
            })}
        </TableRow>
      );
    });
  };

  return (
    <div style={{ width: '100%' }}>
      <div
        style={{
          position: 'relative',
          width: '100%',
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
        }}
      >
        <div
          style={{
            position: 'relative',
            width: '100%',
            marginLeft: '12px',
          }}
        >
          {
            <TableFilter
              filters={header
                ?.filter(
                  (h) => h.isFilterable && h.dataType !== Server.DataType.Image
                )
                .map((h) => {
                  return {
                    key: h.key,
                    label: h.title,
                    dataType: h.dataType,
                  };
                })}
              shownFilters={shownMenuFilters}
              setShownFilters={setShownMenuFilters}
              selectedFilters={selectedFilters}
              setSelectedFilters={setSelectedFilters}
              appliedFilters={appliedFilters}
              setAppliedFilters={setAppliedFilters}
              onChange={onFilterChange}
              defaultFilters={defaultFilters}
            />
          }
          {extraActions ? (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: dir === 'rtl' ? '0' : 'auto',
                right: dir === 'rtl' ? 'auto' : '0',
              }}
            >
              <Dropdown
                id="table-extra-actions"
                label={
                  <div style={{ fontSize: '20px', color: '#343a40' }}>
                    <IoIosSettings />
                  </div>
                }
                items={extraActions}
                buttonStyle={{
                  width: '34px',
                  minWidth: '34px',
                  paddingRight: '2px',
                  paddingLeft: '2px',
                  color: '#343a40',
                }}
              />
            </div>
          ) : null}
        </div>
        <div
          style={{
            position: 'absolute',
            top: 0,
            right: dir === 'rtl' ? '100px' : '0',
            left: dir === 'rtl' ? '0' : '125px',
            width: 'fit-content',
          }}
        >
          <Popover
            buttonText={t(`dropdowns.lists.${'Manage Columns'}`) || ''}
            buttonId="manage-columns"
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            buttonStyle={{
              border: '1px solid #ced4da',
              color: 'black',
              backgroundColor: 'transparent',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: 'transparent',
                boxShadow: 'none',
              },
            }}
          >
            <div style={{ minWidth: '200px', padding: '0 16px' }}>
              <CheckboxGroup
                data={headerOriginal
                  ?.filter((h) => h.title !== 'Id')
                  .map((h) => {
                    return {
                      value: h.key,
                      label: h.title,
                      selected: !h.isHidden,
                    };
                  })}
                onChange={(checkboxes) => {
                  onHeaderManage && onHeaderManage(checkboxes);
                }}
              />
            </div>
          </Popover>
        </div>
      </div>
      <div style={{ marginTop: '16px' }}>
        <TableContainer
          component={Paper}
          sx={{
            boxShadow:
              isLoading && dataTableData
                ? 'none'
                : isLoading && !dataTableData
                ? 'none'
                : !isLoading && dataTableData
                ? '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)'
                : 'none',
            position: 'relative',
          }}
        >
          <Table
            sx={{
              minWidth: 650,
              height:
                isLoading && dataTableData
                  ? 'calc(100vh - 320px)'
                  : isLoading && !dataTableData
                  ? '0px'
                  : !isLoading && dataTableData
                  ? 'calc(100vh - 320px)'
                  : '',
              minHeight:
                isLoading && dataTableData
                  ? '207px'
                  : isLoading && !dataTableData
                  ? '0px'
                  : !isLoading && dataTableData
                  ? '207px'
                  : '',
              fontFamily: 'Tajawal !important',
              // display: isLoading && !dataTableData ? 'none' : 'list-item',
              display: 'list-item',
              overflowX: !isLoading && dataTableData ? 'auto' : 'hidden',
              overflowY: 'auto',
              perspective: '1500px',
              position: 'relative',
            }}
          >
            <TableHead
              sx={{
                width: '100%',
              }}
            >
              <TableRow sx={{ width: '100%' }}>{renderHeader()}</TableRow>
            </TableHead>
            {dataTableData && (
              <>
                <TableBody
                // sx={{
                //   display: isLoading ? 'none' : 'table-row-group',
                // }}
                >
                  {dataTableData.totalCount ? (
                    renderRows()
                  ) : (
                    <div
                      style={{
                        width: '100%',
                        height: '300px',
                        padding: '40px 0',
                        textAlign: 'center',
                        position: 'absolute',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <ReturnTranslatedText
                        origintext="No Data"
                        text={`general.${'No Data'}`}
                      />
                    </div>
                  )}
                </TableBody>
                {isLoading && (
                  <div
                    style={{
                      width: '100%',
                      marginTop: '16px',
                      height: 'calc(100vh - 400px)',
                      opacity: '70%',
                      position: 'absolute',
                      top: '0px',
                    }}
                  >
                    <div
                      style={{
                        width: '100%',
                        marginTop: '24px',
                        fontSize: '24px',
                        textAlign: 'center',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <ReturnTranslatedText
                        origintext="Loading..."
                        text={`general.${'Loading'}`}
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </Table>
        </TableContainer>
        {isLoading && !dataTableData && (
          <div
            style={{
              width: '100%',
              marginTop: '16px',
              height: 'calc(100vh - 400px)',
            }}
          >
            <div
              style={{
                width: '100%',
                marginTop: '24px',
                fontSize: '24px',
                textAlign: 'center',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <ReturnTranslatedText
                origintext="Loading..."
                text={`general.${'Loading'}`}
              />
            </div>
          </div>
        )}
      </div>
      {onPaginationChange && (
        <TablePagination
          pagination={{
            pageNumber: Number(pageSearchParam),
            pageSize: Number(pageSizeSearchParam),
            totalCount: dataTableData?.totalCount,
          }}
          onChange={(pagination) => {
            setSearchParams({
              page: pagination.pageNumber.toString(),
              pageSize: pagination.pageSize.toString(),
            });

            onPaginationChange(pagination);
          }}
        />
      )}
    </div>
  );
};
